/**
 * The bottom rail under the canvas — the `Builder planu — tydzień + programator` handoff's own
 * volume bar, and the same row under both canvases.
 *
 * ── What it draws now, and what it used to ───────────────────────────────────────────────────
 * Until 7 September 2026 this was six SHARE bars: a group's absolute points, its whole-number
 * share of the week, and a bar scaled against the heaviest group. The handoff replaces all of it
 * with a judgement — each group's volume against its own MEV / MAV / MRV, a track that ends at
 * MRV, two hairline ticks, and one word saying which band the week is in. The share went with the
 * bars; the day card's own load module still prints it per day.
 *
 * The decision behind the bar is `model/volumeStatus.ts` and none of it is here. This component
 * reads a `VolumeRailView` and binds a status NAME to a class.
 *
 * ── It waits for the CATALOGUE, and the box does not move while it waits ─────────────────────
 * The points are attributed through a second read, and until it lands every group's load is zero —
 * six cells reading `0` and "poniżej MEV" over a fully programmed week, silently rewriting
 * themselves a moment later. So the rail is drawn from the first frame at its full height, the six
 * cells keep their slots, and a gated skeleton (`useSkeletonGate`: 150ms of grace, 300ms of minimum
 * life) stands where the name, the number, the bar and the word will be. `aria-busy` plus one
 * visually-hidden sentence is what a screen reader gets, which is the pair every skeleton in this
 * app carries — and both of those key on `gate.waiting`, the same value the cells fork on (owner,
 * 21 September 2026). The bars outlive the answer by their minimum life, and while they stand the
 * six cells still hold placeholders and are still about to change: reporting the rail settled at
 * the moment the read landed is a claim a listener acts on 300ms early.
 *
 * ── The gate stands IN FRONT of the cell's `null`, and may not replace it ────────────────────
 * A cell's `value`, `status` and `statusLabel` are `null` for two different reasons, and
 * `model/volumeStatus.ts` says so: the catalogue read is open, OR the phase's cycle is zero days
 * long — a sequence phase whose every day the save would drop. The cell has exactly ONE shape for
 * "no honest number yet", which is why the two collapse there. Keying the fold on the gate instead
 * would draw the second case as a wait that never ends, so the fork is `gate.waiting || … === null`
 * and the content carries `gate.reveal` on its first render. Splitting the two nulls into a
 * discriminated reading is a separate change and has not been made.
 *
 * The cells are ordered by volume, so their NAMES arrive with their numbers rather than shuffling
 * under the trainer — which is why the skeleton covers the label too and not only the value.
 *
 * ── The 1,0 / 0,5 weighting is under the caption's ⓘ ─────────────────────────────────────────
 * It was a printed sentence under the old share bars and went with them, leaving "11,5" with
 * nothing on screen to explain that a secondary muscle counts half. The owner's standing rule is
 * that an explanation lives under an ⓘ or it goes, and this is the ⓘ.
 *
 * ── The over-MRV bar is `--danger`, not the mock's ember ─────────────────────────────────────
 * The handoff paints "ponad MRV" in `#ff5a1f`, which is the accent. This app spends four accent
 * marks a screen (view ADR §1) and they are already spoken for — the active phase pip, the lock
 * chip, whatever holds focus, and Zapisz once it can act. A muscle bar wearing the fifth would
 * compete with the trainer's own pending action and mean neither thing. `--danger` is what an
 * over-recovery week actually is, and it is the same substitution the muscle scale itself makes by
 * excluding ember outright.
 *
 * ── Two things the mock does not draw, and they are CUT ──────────────────────────────────────
 * The handoff's rail is the caption block and the six cells, full stop. Until 7 September 2026 this
 * row also carried the AVERAGE SESSION LENGTH and the plan's SYSTEM control, pushed to the right
 * end and costing the six cells roughly 200px. The owner weighed that on 7 September 2026 and cut
 * both: the mock gives the whole width to the muscle cells and that is what ships.
 *
 * **The cut leaves a real gap, and it is recorded rather than worked around.** The system control
 * was the ONE place in this app a plan's scheduling mode was stated (`self-paced-plan-requirements.md`
 * R1 makes it a fact settled at creation and never changed afterwards), so the mode is now stated
 * and changeable NOWHERE. The owner accepted that knowingly and it is an open item for a later
 * round. It is not to be smuggled onto another surface to be helpful — the next home for it is a
 * decision, not an improvisation. The average session length went the same way and has no other
 * home either; the day column header still prints each day's own "~62 min".
 */

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { muscleColour } from '~/shared/ui/muscleColour';
import skel from '~/shared/ui/skeleton.module.css';

import type { VolumeCellView, VolumeRailView } from '../model/volumeStatus';

import styles from './BuilderRail.module.css';

/** The four bands, each bound to the pair of tokens its fill and its word need. */
const STATUS_CLASS: Record<NonNullable<VolumeCellView['status']>, string | undefined> = {
  belowMev: styles.belowMev,
  inRange: styles.inRange,
  aboveMav: styles.aboveMav,
  aboveMrv: styles.aboveMrv,
};

interface BuilderRailProps {
  readonly volume: VolumeRailView;
}

export function BuilderRail({ volume }: BuilderRailProps) {
  const gate = useSkeletonGate(volume.isPending);

  return (
    <div className={styles.rail} aria-busy={gate.waiting || undefined}>
      <div className={styles.caption}>
        <span className={styles.captionTitle}>{volume.title}</span>
        {/* Two BLOCK lines rather than one text run with a `<br>` in it. The break was visual only:
          * an accessible-name computation concatenates the two text nodes with nothing between
          * them, and the caption spoke as "MEV → MAV → MRVna 7 dni · cykl 5 dni". A block boundary
          * is the separator, and it draws the identical two lines. */}
        <span className={styles.captionSub}>
          <span className={styles.captionLine}>{volume.scale}</span>
          <span className={styles.captionLine}>{volume.unit}</span>
        </span>
      </div>

      {/* The word beside `aria-busy`, on the same value, so the pair cannot disagree. It leaves
       * with the bars rather than with the read — once the six cells can speak for themselves,
       * nothing lingers in the accessibility tree. */}
      {gate.waiting && <span className={styles.hidden}>{volume.pendingLabel}</span>}

      <ul className={styles.cells}>
        {volume.cells.map((cell) => {
          // The wait and the silent cell draw the same thing, and the cell's own `null` is the
          // only one of the two that can outlast the gate. See the header.
          const noNumber = gate.waiting || cell.value === null;
          // The band the cell may PAINT: withheld for the whole wait, and absent on its own
          // whenever the cell has no honest number behind it.
          const band = gate.waiting ? null : cell.status;
          return (
            <li
              key={cell.group}
              className={`${String(styles.cell)} ${
                band === null ? '' : String(STATUS_CLASS[band])
              }`}
            >
              <span className={styles.head}>
                {/* The group's colour, and nothing is read from it: the name is right beside it. */}
                <i
                  className={styles.dot}
                  style={{
                    background: band === null ? undefined : muscleColour(cell.group),
                  }}
                  aria-hidden="true"
                />
                {noNumber ? (
                  /* The GATE hides the bar, never the box: `.labelBar` reserves the row on the very
                   * first frame and `skel.bar` only fills it once the wait has earned a skeleton. */
                  <i
                    className={`${String(styles.labelBar)} ${gate.skeleton ? String(skel.bar) : ''}`}
                    aria-hidden="true"
                  />
                ) : (
                  <>
                    <span className={`${String(styles.label)} ${gate.reveal}`}>{cell.label}</span>
                    {/* The mock's own `<span style="flex:1">`, which is what pushes the number to the
                     * cell's right edge. */}
                    <span className={styles.spacer} />
                    <b className={`${String(styles.value)} ${gate.reveal}`}>{cell.value}</b>
                  </>
                )}
              </span>
              {/* Hidden from assistive technology: the group, its number and its band are all
               * ordinary text on this cell, so the track would announce a row already read. */}
              <span className={styles.track} aria-hidden="true">
                {band !== null && (
                  <>
                    <i className={styles.fill} style={{ width: `${String(cell.fillPercent)}%` }} />
                    <i className={styles.tickMev} style={{ left: `${String(cell.mevPercent)}%` }} />
                    <i className={styles.tickMav} style={{ left: `${String(cell.mavPercent)}%` }} />
                  </>
                )}
              </span>
              {gate.waiting || cell.statusLabel === null ? (
                <i
                  className={`${String(styles.statusBar)} ${gate.skeleton ? String(skel.bar) : ''}`}
                  aria-hidden="true"
                />
              ) : (
                <span className={`${String(styles.status)} ${gate.reveal}`}>{cell.statusLabel}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
